import type {
  LabManagedQuiz,
  LabPublishedItem,
  LabReleaseStatus,
  LabReviewQueueItem,
  LabReviewRelease,
  LabReviewScope,
  LabUnitSummary,
  LabVisibility,
} from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LabsDemoHandler = (body: any) => { success: true; data: unknown };

export interface LabsDemoOptions {
  actorName: string;
  findUnit: (unitId: string) => { code: string; name: string } | undefined;
  reviewableUnitIds?: () => string[];
}

const ok = <T,>(data: T) => ({ success: true as const, data });
const DAY_MS = 86_400_000;
const daysAgo = (days: number) => new Date(Date.now() - days * DAY_MS).toISOString();
const nowIso = () => new Date().toISOString();
const NOT_IN_DEMO = "This isn't available in demo mode";

type Member = LabReviewRelease['members'][number];
type Event = LabReviewRelease['events'][number];

interface DemoProject {
  projectId: string;
  releaseId: string;
  unitId: string | null;
  title: string;
  version: string;
  visibility: LabVisibility;
  status: LabReleaseStatus;
  description: string;
  engine: 'unity' | 'godot';
  platforms: string[];
  submittedAt: string;
  submittedBy: string;
  publishedAt: string | null;
  members: Member[];
  events: Event[];
}

const member = (name: string, role: Member['role']): Member => ({
  userId: `demo-${name.toLowerCase().replace(/\s+/g, '-')}`,
  name,
  email: `${name.toLowerCase().replace(/\s+/g, '.')}@demo-students.ludolio.com`,
  role,
});

const submitted = (actor: string, days: number): Event => ({ action: 'submitted', note: null, createdAt: daysAgo(days), actor });
const approved = (days: number): Event => ({ action: 'approved', note: null, createdAt: daysAgo(days), actor: 'Casey Morgan' });

const seedProjects = (): DemoProject[] => [
  {
    projectId: 'demo-lab-project-1',
    releaseId: 'demo-lab-release-1',
    unitId: 'unit-0001',
    title: 'Lantern Drift',
    version: '1.2.0',
    visibility: 'organization',
    status: 'in_review',
    description: 'Guide a paper lantern down a river at night. Light the shrines before the wind puts you out.',
    engine: 'unity',
    platforms: ['windows-x64', 'mac-universal'],
    submittedAt: daysAgo(2),
    submittedBy: 'Alex Adams',
    publishedAt: null,
    members: [member('Alex Adams', 'owner'), member('Blake Brooks', 'member')],
    events: [submitted('Alex Adams', 2)],
  },
  {
    projectId: 'demo-lab-project-2',
    releaseId: 'demo-lab-release-2',
    unitId: 'unit-0002',
    title: 'Signal Lost',
    version: '0.9.0',
    visibility: 'unit',
    status: 'in_review',
    description: 'A short radio puzzle game. Tune between stations to find the missing crew.',
    engine: 'godot',
    platforms: ['windows-x64'],
    submittedAt: daysAgo(1),
    submittedBy: 'Dana Diaz',
    publishedAt: null,
    members: [member('Dana Diaz', 'owner'), member('Elliot Evans', 'manager'), member('Fin Flores', 'member')],
    events: [submitted('Dana Diaz', 1)],
  },
  {
    projectId: 'demo-lab-project-3',
    releaseId: 'demo-lab-release-3',
    unitId: 'unit-0001',
    title: 'Paper Orchard',
    version: '1.0.0',
    visibility: 'organization',
    status: 'published',
    description: 'Fold, plant and trade paper trees to grow an orchard before winter.',
    engine: 'unity',
    platforms: ['windows-x64', 'mac-universal'],
    submittedAt: daysAgo(14),
    submittedBy: 'Chris Clark',
    publishedAt: daysAgo(12),
    members: [member('Chris Clark', 'owner'), member('Dana Diaz', 'member')],
    events: [submitted('Chris Clark', 14), approved(12)],
  },
  {
    projectId: 'demo-lab-project-4',
    releaseId: 'demo-lab-release-4',
    unitId: 'unit-0002',
    title: 'Tidewalker',
    version: '2.1.0',
    visibility: 'unit',
    status: 'published',
    description: 'Cross a coastline between tides, using rock pools as stepping stones.',
    engine: 'godot',
    platforms: ['windows-x64', 'linux-x64'],
    submittedAt: daysAgo(22),
    submittedBy: 'Harper Hale',
    publishedAt: daysAgo(20),
    members: [member('Harper Hale', 'owner')],
    events: [submitted('Harper Hale', 22), approved(20)],
  },
  {
    projectId: 'demo-lab-project-5',
    releaseId: 'demo-lab-release-5',
    unitId: 'unit-0001',
    title: 'Clockwork Choir',
    version: '1.0.0',
    visibility: 'organization',
    status: 'published',
    description: 'Wind up mechanical singers and keep them in time for the evening concert.',
    engine: 'unity',
    platforms: ['windows-x64'],
    submittedAt: daysAgo(33),
    submittedBy: 'Gale Garza',
    publishedAt: daysAgo(30),
    members: [member('Gale Garza', 'owner'), member('Indie Irwin', 'member')],
    events: [submitted('Gale Garza', 33), approved(30)],
  },
];

export const DEMO_PLAYTEST_QUIZ_ID = 'demo-lab-quiz-1';
const seedQuizzes = (): Record<string, LabManagedQuiz[]> => ({
  'demo-lab-project-3': [{
    id: DEMO_PLAYTEST_QUIZ_ID,
    title: 'Playtest feedback',
    status: 'open',
    totalResponses: 3,
    currentVersion: { id: 'demo-lab-quiz-1-v1', version: 1, responseCount: 3 },
    draft: {
      description: null,
      questions: [
        { id: 'q1', kind: 'single_choice', prompt: 'Was the game fun?', required: true, options: [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'Not yet' }] },
        { id: 'q2', kind: 'short_text', prompt: 'What should we change?', required: false },
      ],
    },
  }],
  'demo-lab-project-1': [{
    id: 'demo-lab-quiz-2',
    title: 'Shrine run check-in',
    status: 'draft',
    totalResponses: 0,
    currentVersion: null,
    draft: {
      description: 'Answer after your first run down the river.',
      questions: [
        { id: 'q1', kind: 'single_choice', prompt: 'How many shrines did you light?', required: true, options: [{ id: 'few', label: '1 to 3' }, { id: 'most', label: '4 to 6' }, { id: 'all', label: 'All of them' }] },
        { id: 'q2', kind: 'multi_choice', prompt: 'What helped you read the wind?', required: false, options: [{ id: 'sound', label: 'Sound' }, { id: 'leaves', label: 'Falling leaves' }, { id: 'flame', label: 'The lantern flame' }] },
        { id: 'q3', kind: 'short_text', prompt: 'Anything else we should know?', required: false },
      ],
    },
  }],
});

export function createLabsDemo({ actorName, findUnit, reviewableUnitIds }: LabsDemoOptions): Record<string, LabsDemoHandler> {
  const projects = seedProjects();
  const quizzesByProject = seedQuizzes();
  const isOrgAdmin = !reviewableUnitIds;

  const unitLabsEnabled = new Map<string, boolean>([
    ['unit-0001', true],
    ['unit-0002', true],
  ]);

  const canReview = (project: DemoProject) =>
    isOrgAdmin || (project.unitId !== null && reviewableUnitIds().includes(project.unitId));

  const listable = (project: DemoProject, unitId?: string) =>
    canReview(project) && (!unitId || project.unitId === unitId);

  const unitSummary = (unitId: string | null): LabUnitSummary | null => {
    if (!unitId) return null;
    const unit = findUnit(unitId);
    return { id: unitId, code: unit?.code ?? null, name: unit?.name ?? null };
  };

  const findRelease = (releaseId: string) => {
    const project = projects.find((item) => item.releaseId === releaseId);
    if (!project) throw new Error('Release not found');
    if (!canReview(project)) throw new Error('You cannot review this project');
    return project;
  };

  const toQueueItem = (project: DemoProject): LabReviewQueueItem => ({
    releaseId: project.releaseId,
    projectId: project.projectId,
    title: project.title,
    version: project.version,
    visibility: project.visibility,
    iconUrl: null,
    submittedAt: project.submittedAt,
    submittedBy: project.submittedBy,
    unit: unitSummary(project.unitId),
    platforms: project.platforms,
  });

  const toPublishedItem = (project: DemoProject): LabPublishedItem => ({
    releaseId: project.releaseId,
    projectId: project.projectId,
    title: project.title,
    version: project.version,
    visibility: project.visibility,
    iconUrl: null,
    publishedAt: project.publishedAt,
    submittedBy: project.submittedBy,
    unit: unitSummary(project.unitId),
    platforms: project.platforms,
  });

  const toRelease = (project: DemoProject): LabReviewRelease => ({
    project: { id: project.projectId, unit: unitSummary(project.unitId), appId: null },
    release: {
      id: project.releaseId,
      version: project.version,
      status: project.status,
      visibility: project.visibility,
      title: project.title,
      description: project.description,
      credits: project.members.map((item) => ({ name: item.name, contribution: item.role === 'owner' ? 'Lead' : 'Design and art' })),
      contentNotes: null,
      iconUrl: null,
      bannerUrl: null,
      submittedAt: project.submittedAt,
      builds: project.platforms.map((platform, index) => ({
        id: `${project.releaseId}-build-${index}`,
        platform,
        status: 'passed',
        fileSize: (180 + index * 40) * 1024 * 1024,
        executablePath: platform.startsWith('mac') ? `${project.title}.app` : `${project.title}.exe`,
      })),
      sourcePackage: {
        id: `${project.releaseId}-source`,
        status: 'passed',
        fileSize: 420 * 1024 * 1024,
        engine: project.engine,
        engineVersion: project.engine === 'unity' ? '6000.0' : '4.3',
        fileCount: 1840,
      },
    },
    members: project.members,
    events: project.events,
    canDecide: project.status === 'in_review',
    lineage: { ancestors: [] },
    remix: { permission: 'none', termsVersion: null, assetSharingConfirmed: false, structureConfirmed: null },
  });

  const publishedInUnit = (unitId: string) =>
    projects.filter((project) => project.unitId === unitId && project.status === 'published');

  const takeDown = (project: DemoProject, note: string) => {
    project.status = 'withdrawn';
    project.events.push({ action: 'taken_down', note, createdAt: nowIso(), actor: actorName });
  };

  const unitLabsSettings = (unitId: string) => ({
    available: true,
    canManage: true,
    enabled: unitLabsEnabled.get(unitId) ?? false,
    publishedCount: publishedInUnit(unitId).length,
  });

  const handlers: Record<string, LabsDemoHandler> = {
    '/labs/availability': () => ok({ available: true }),

    '/labs/review/queue': ({ scope, unitId }: { scope?: LabReviewScope; unitId?: string }) => {
      if (scope === 'organization' && !isOrgAdmin) throw new Error('Only organization administrators can see every submission');
      return ok(projects.filter((project) => project.status === 'in_review' && listable(project, unitId)).map(toQueueItem));
    },
    '/labs/review/published': ({ scope, unitId }: { scope?: LabReviewScope; unitId?: string }) => {
      if (scope === 'organization' && !isOrgAdmin) throw new Error('Only organization administrators can see every submission');
      return ok(
        projects
          .filter((project) => project.status === 'published' && listable(project, unitId))
          .sort((a, b) => (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''))
          .map(toPublishedItem),
      );
    },
    '/labs/review/takedown': ({ projectId, note }) => {
      const project = projects.find((item) => item.projectId === projectId && item.status === 'published');
      if (!project) throw new Error('This game is not published');
      if (!canReview(project)) throw new Error('You can only take down projects from units you teach');
      takeDown(project, note);
      return ok(null);
    },
    '/labs/review/release': ({ releaseId }) => ok(toRelease(findRelease(releaseId))),
    '/labs/quizzes/list': ({ projectId }) => {
      const project = projects.find((item) => item.projectId === projectId);
      if (!project) throw new Error('Project not found');
      if (!canReview(project)) throw new Error("You can't see this project's quizzes");
      return ok(quizzesByProject[projectId] ?? []);
    },
    '/labs/review/decide': ({ releaseId, action, note }) => {
      const project = findRelease(releaseId);
      if (project.status !== 'in_review') throw new Error('This release is no longer waiting for review');
      if (action === 'approve') {
        project.status = 'published';
        project.publishedAt = nowIso();
        project.events.push({ action: 'approved', note: note ?? null, createdAt: nowIso(), actor: actorName });
      } else {
        if (!note) throw new Error('Add a note for the team');
        project.status = 'changes_requested';
        project.events.push({ action: 'changes_requested', note, createdAt: nowIso(), actor: actorName });
      }
      return ok(toRelease(project));
    },
    '/labs/remixes/source': () => {
      throw new Error(NOT_IN_DEMO);
    },

    '/labs/units/settings/get': ({ unitId }) => ok(unitLabsSettings(unitId)),
    '/labs/units/settings/update': ({ unitId, enabled, takeDownPublished }) => {
      unitLabsEnabled.set(unitId, Boolean(enabled));
      if (!enabled && takeDownPublished) {
        for (const project of publishedInUnit(unitId)) takeDown(project, 'Labs was turned off for this unit');
      }
      return ok(unitLabsSettings(unitId));
    },
  };

  return handlers;
}
